import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    FormControl,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    Alert,
    Button,
    Stepper,
    Step,
    StepLabel,
    LinearProgress,
    Stack,
    Tooltip,
    IconButton,
    InputLabel,
    FormHelperText,
    useMediaQuery,
    useTheme,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../../../../utils/enums/CloudFunctions';
import { useToast } from '../../../../context/toast/ToastContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { Info, Add, Delete } from '@mui/icons-material';
import { USAbbreviations } from '../../../../utils/select_helpers/UnitedStatesAbbreviations';

const InfoTooltip = ({ title }) => (
    <Tooltip title={title}>
        <IconButton size="small" sx={{ ml: 1 }}>
            <Info fontSize="small" />
        </IconButton>
    </Tooltip>
);

// Template for a new registration
const createNewRegistration = () => ({
    id: Date.now().toString(), // Unique ID for UI management
    country: 'US',
    state: '',
    registrationNumber: '',
    active: true,
    active_from: 'now', // Using Stripe's API format
    type: 'standard', // Default registration type
});

// Determine if a state supports retail delivery fee
const stateSupportsRetailDeliveryFee = (state) => {
    return ['CO', 'MN'].includes(state);
};

// Get available registration types for a state
const getRegistrationTypesForState = (state) => {
    const types = [
        { value: 'standard', label: 'Standard' },
        { value: 'marketplace_facilitator', label: 'Marketplace Facilitator' }
    ];
    
    if (stateSupportsRetailDeliveryFee(state)) {
        types.push({ value: 'state_retail_delivery_fee', label: 'Retail Delivery Fee' });
    }
    
    return types;
};

const TaxSetupGuide = ({ onSave }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { organizationStripeInfo } = useOrganizationContext();
    const { firebaseUser } = useUserContext();
    const { showToast } = useToast();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [taxSettings, setTaxSettings] = useState({
        enabled: false,
        automaticTax: true,
        taxIdCollection: true,
        defaultTaxBehavior: 'exclusive',
        federalEIN: '',
        registrations: [],
        setupCompleted: false
    });

    // Current registration being edited
    const [currentRegistration, setCurrentRegistration] = useState(createNewRegistration());
    const [editingExistingRegistration, setEditingExistingRegistration] = useState(false);

    useEffect(() => {
        const loadExistingTaxSettings = async () => {
            try {
                const db = getFirestore();
                const stripeInfoRef = doc(db, 'organizations', firebaseUser.organization, 'stripe_information', 'stripe_information');
                const stripeInfoDoc = await getDoc(stripeInfoRef);
                
                if (stripeInfoDoc.exists() && stripeInfoDoc.data().tax_settings) {
                    const existingSettings = stripeInfoDoc.data().tax_settings;
                    setTaxSettings(prev => ({
                        ...prev,
                        enabled: existingSettings.enabled || false,
                        automaticTax: existingSettings.automatic_tax || true,
                        taxIdCollection: existingSettings.tax_id_collection || true,
                        defaultTaxBehavior: existingSettings.default_tax_behavior || 'exclusive',
                        federalEIN: existingSettings.federal_ein || '',
                        registrations: existingSettings.registrations || [],
                        setupCompleted: existingSettings.setup_completed || false
                    }));

                    // If setup was completed, move to the success step
                    if (existingSettings.setup_completed) {
                        setActiveStep(3);
                    }
                }
            } catch (error) {
                console.error('Error loading tax settings:', error);
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), 'error');
            } finally {
                setInitialLoading(false);
            }
        };

        if (firebaseUser?.organization) {
            loadExistingTaxSettings();
        }
    }, [firebaseUser?.organization]);

    const handleNext = () => {
        if (activeStep === 2) {
            // Format and save the tax settings according to Stripe's API format
            const formattedRegistrations = taxSettings.registrations.map(reg => ({
                country: reg.country,
                active_from: 'now',
                country_options: {
                    us: {
                        state: reg.state,
                        type: reg.type || 'standard',
                        registration_number: reg.registrationNumber
                    }
                }
            }));
            
            const finalSettings = {
                ...taxSettings,
                registrations: formattedRegistrations,
                setupCompleted: true,
            };
            
            onSave(finalSettings);
        } else {
            setActiveStep((prevStep) => prevStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleTaxSettingChange = (field, value) => {
        setTaxSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRegistrationChange = (field, value) => {
        // If changing state, update available registration types
        if (field === 'state') {
            setCurrentRegistration(prev => ({
                ...prev,
                [field]: value,
                // Reset type if the new state doesn't support the current type
                type: prev.type === 'state_retail_delivery_fee' && !stateSupportsRetailDeliveryFee(value) 
                    ? 'standard' 
                    : prev.type
            }));
        } else {
            setCurrentRegistration(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const addRegistration = () => {
        // Validate the current registration
        if (!currentRegistration.state || !currentRegistration.registrationNumber) {
            showToast(t(StringKeys.PLEASE_COMPLETE_ALL_REQUIRED_FIELDS), 'error');
            return;
        }

        if (editingExistingRegistration) {
            // Update existing registration
            setTaxSettings(prev => ({
                ...prev,
                registrations: prev.registrations.map(reg => 
                    reg.id === currentRegistration.id ? currentRegistration : reg
                )
            }));
            setEditingExistingRegistration(false);
        } else {
            // Add new registration
            setTaxSettings(prev => ({
                ...prev,
                registrations: [...prev.registrations, currentRegistration]
            }));
        }

        // Reset the form
        setCurrentRegistration(createNewRegistration());
    };

    const editRegistration = (registrationId) => {
        const registrationToEdit = taxSettings.registrations.find(reg => reg.id === registrationId);
        if (registrationToEdit) {
            setCurrentRegistration(registrationToEdit);
            setEditingExistingRegistration(true);
        }
    };

    const removeRegistration = (registrationId) => {
        setTaxSettings(prev => ({
            ...prev,
            registrations: prev.registrations.filter(reg => reg.id !== registrationId)
        }));
    };

    const steps = [
        {
            label: t(StringKeys.TAX_SETUP_STEP_1),
            content: (
                <Card>
                    <CardContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Stripe Tax is free until you add your first live registration. You'll see zero tax amounts for jurisdictions where you're not registered to collect tax.
                        </Alert>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {t(StringKeys.TAX_SETUP_DESCRIPTION)}
                        </Typography>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Before proceeding, ensure you have:</Typography>
                            <ul>
                                <li>A verified Stripe account with tax enabled</li>
                                <li>Your US tax registration numbers (EIN/Tax ID)</li>
                                <li>Your business address verified in Stripe</li>
                                <li>Documentation of your state tax obligations</li>
                            </ul>
                        </Alert>
                    </CardContent>
                </Card>
            )
        },
        {
            label: t(StringKeys.TAX_REGISTRATION_REQUIRED),
            content: (
                <Card>
                    <CardContent>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Tax will only be calculated for states where you have provided valid registration details. You can register for multiple states where you have tax nexus.
                        </Alert>
                        
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Federal Tax ID (EIN)"
                                    variant="outlined"
                                    value={taxSettings.federalEIN || ''}
                                    onChange={(e) => handleTaxSettingChange('federalEIN', e.target.value)}
                                    helperText="Your 9-digit Employer Identification Number (XX-XXXXXXX)"
                                />
                            </Grid>
                        </Grid>
                        
                        <Typography variant="h6" gutterBottom>
                            State Tax Registrations
                            <InfoTooltip title="Add all states where you have tax nexus or are required to collect sales tax" />
                        </Typography>
                        
                        {/* Registration list */}
                        {taxSettings.registrations.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <List>
                                        {taxSettings.registrations.map((registration) => (
                                            <ListItem 
                                                key={registration.id}
                                                secondaryAction={
                                                    <Box>
                                                        <IconButton 
                                                            edge="end" 
                                                            aria-label="edit"
                                                            onClick={() => editRegistration(registration.id)}
                                                        >
                                                            <Info />
                                                        </IconButton>
                                                        <IconButton 
                                                            edge="end" 
                                                            aria-label="delete"
                                                            onClick={() => removeRegistration(registration.id)}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Box>
                                                }
                                            >
                                                <ListItemText 
                                                    primary={`${registration.state} - ${registration.registrationNumber}`}
                                                    secondary={registration.type === 'standard' ? 'Standard Registration' : 'Marketplace Facilitator'}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Box>
                        )}
                        
                        {/* Add/edit registration form */}
                        <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                {editingExistingRegistration ? 'Edit Registration' : 'Add New Registration'}
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="State"
                                        variant="outlined"
                                        value={currentRegistration.state || ''}
                                        onChange={(e) => handleRegistrationChange('state', e.target.value)}
                                        helperText="Select the state where you're registering"
                                    >
                                        {USAbbreviations.map((abbreviation) => (
                                            <MenuItem key={abbreviation} value={abbreviation}>
                                                {abbreviation}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="State Tax Registration Number"
                                        variant="outlined"
                                        value={currentRegistration.registrationNumber || ''}
                                        onChange={(e) => handleRegistrationChange('registrationNumber', e.target.value)}
                                        helperText="Your state tax permit or registration number"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Registration Type"
                                        variant="outlined"
                                        value={currentRegistration.type || 'standard'}
                                        onChange={(e) => handleRegistrationChange('type', e.target.value)}
                                        helperText={currentRegistration.type === 'state_retail_delivery_fee' ? 
                                            "Required for delivery services in CO and MN" : 
                                            "Type of registration"}
                                    >
                                        {getRegistrationTypesForState(currentRegistration.state).map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button 
                                        variant="contained" 
                                        color="primary"
                                        onClick={addRegistration}
                                        startIcon={editingExistingRegistration ? null : <Add />}
                                    >
                                        {editingExistingRegistration ? 'Update Registration' : 'Add Registration'}
                                    </Button>
                                    {editingExistingRegistration && (
                                        <Button 
                                            variant="outlined" 
                                            color="secondary"
                                            onClick={() => {
                                                setCurrentRegistration(createNewRegistration());
                                                setEditingExistingRegistration(false);
                                            }}
                                            sx={{ ml: 1 }}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>

                        <Alert severity="warning" sx={{ mt: 3 }}>
                            <Typography variant="body2">
                                <strong>Important:</strong> Registration requirements vary by state. Some states may require additional forms or documentation. Consult with a tax professional to ensure compliance with all state requirements.
                            </Typography>
                        </Alert>

                        {/* Colorado and Minnesota Retail Delivery Fee Alert, only show when a US registration exists */}
                        {taxSettings.registrations.some(reg => reg.country === 'US') && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Special Tax Types:</strong> Colorado and Minnesota require a Retail Delivery Fee registration for businesses that deliver tangible personal property by motor vehicle. If you deliver in either state, be sure to add this specific registration type.
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )
        },
        {
            label: t(StringKeys.TAX_CALCULATION_METHOD),
            content: (
                <Card>
                    <CardContent>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Configure how sales tax will be calculated and displayed to your US customers.
                        </Alert>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box sx={{ mb: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={taxSettings.automaticTax}
                                                onChange={(e) => handleTaxSettingChange('automaticTax', e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                {t(StringKeys.TAX_CALCULATION_AUTOMATIC)}
                                                <InfoTooltip title="Stripe automatically calculates state and local sales tax based on your registrations" />
                                            </Box>
                                        }
                                    />
                                    <FormHelperText>
                                        Automatically calculates correct tax rates for each US state and locality
                                    </FormHelperText>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ mb: 3 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={taxSettings.taxIdCollection}
                                                onChange={(e) => handleTaxSettingChange('taxIdCollection', e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                {t(StringKeys.TAX_ID_COLLECTION)}
                                                <InfoTooltip title="Collect and validate tax exemption certificates from qualified businesses" />
                                            </Box>
                                        }
                                    />
                                    <FormHelperText>
                                        Enable collection of resale certificates and tax exemption documentation
                                    </FormHelperText>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>{t(StringKeys.TAX_BEHAVIOR)}</InputLabel>
                                    <Select
                                        value={taxSettings.defaultTaxBehavior}
                                        onChange={(e) => handleTaxSettingChange('defaultTaxBehavior', e.target.value)}
                                        label={t(StringKeys.TAX_BEHAVIOR)}
                                    >
                                        <MenuItem value="exclusive">{t(StringKeys.TAX_EXCLUSIVE)}</MenuItem>
                                        <MenuItem value="inclusive">{t(StringKeys.TAX_INCLUSIVE)}</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        <Typography component="div">
                                            • Exclusive: Tax is added on top of prices (standard in US)<br />
                                            • Inclusive: Prices include tax (uncommon in US)
                                        </Typography>
                                    </FormHelperText>
                                </FormControl>
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Note: Tax behavior cannot be changed after it's set for a price
                                </Alert>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )
        },
        {
            label: t(StringKeys.TAX_SETUP_SUCCESS),
            content: (
                <Card>
                    <CardContent>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {t(StringKeys.TAX_SETUP_SUCCESS)}
                        </Alert>
                        
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            Your Tax Registrations
                        </Typography>
                        
                        {taxSettings.registrations.length > 0 ? (
                            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                <List>
                                    {taxSettings.registrations.map((registration) => (
                                        <ListItem key={registration.id}>
                                            <ListItemText 
                                                primary={`${registration.state} - ${registration.registrationNumber}`}
                                                secondary={registration.type === 'standard' ? 'Standard Registration' : 'Marketplace Facilitator'}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                No tax registrations have been added. Tax will not be calculated or collected.
                            </Alert>
                        )}
                        
                        <Typography variant="body1">
                            {t(StringKeys.TAX_SETUP_SUCCESS_DESCRIPTION)}
                            <ul>
                                <li>{t(StringKeys.TAX_SETUP_SUCCESS_POINT_1)}</li>
                                <li>{t(StringKeys.TAX_SETUP_SUCCESS_POINT_2)}</li>
                                <li>{t(StringKeys.TAX_SETUP_SUCCESS_POINT_3)}</li>
                            </ul>
                        </Typography>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Remember to keep your tax registrations up to date and monitor your tax thresholds in the Stripe Dashboard.
                        </Alert>
                    </CardContent>
                </Card>
            )
        }
    ];

    if (initialLoading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box>
            {isMobile ? (
                // Simplified mobile stepper design - only circles with numbers/checkmarks
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                        {steps.map((step, index) => (
                            <Box 
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: index === activeStep 
                                            ? '#1976d2' 
                                            : index < activeStep 
                                                ? '#2e7d32' 
                                                : '#e0e0e0',
                                        color: index <= activeStep ? '#ffffff' : '#757575',
                                        fontWeight: 'bold',
                                        m: 1,
                                        boxShadow: index <= activeStep ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                >
                                    {index < activeStep ? '✓' : index + 1}
                                </Box>
                                {index < steps.length - 1 && (
                                    <Box 
                                        sx={{ 
                                            height: 2, 
                                            width: 20, 
                                            backgroundColor: index < activeStep ? '#2e7d32' : '#e0e0e0',
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            ) : (
                // Desktop stepper with labels
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            <Box sx={{ mt: 2, mb: 4 }}>
                {steps[activeStep].content}
            </Box>

            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
                <Box>
                    {activeStep > 0 && (
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                        >
                            {t(StringKeys.PREVIOUS)}
                        </Button>
                    )}
                </Box>
                <Box>
                    {activeStep < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={activeStep === 1 && taxSettings.registrations.length === 0}
                        >
                            {activeStep === 2 ? t(StringKeys.SAVE) : t(StringKeys.NEXT)}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={() => setActiveStep(0)}
                        >
                            {t(StringKeys.START_OVER)}
                        </Button>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default TaxSetupGuide; 